"use client";

import React from "react";
import { Send, CircleCheck, CircleAlert, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
const DATA_CATEGORIES = ["Notarías", "Inmobiliarias", "Joyeros", "Casas de Empeño", "Servicios Profesionales", "Otros Sectores Art. 17"];
function DataRequestFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground">Solicitud Enviada</h3>
        <p className="text-muted-foreground max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md font-default border-surface hover:bg-surface">
          Nueva Consulta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 3,
          message: "El nombre debe tener al menos 3 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Ej. Lic. Roberto García" className="font-default rounded-md border-surface bg-background focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs text-muted-foreground">Ingresa tu nombre completo o razón social.</FormDescription>
              <FormMessage className="font-default text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ingresa un correo válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="contacto@empresa.com" className="font-default rounded-md border-surface bg-background focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs text-muted-foreground">Para enviarte la propuesta técnica.</FormDescription>
              <FormMessage className="font-default text-destructive" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Empresa <span className="text-muted-foreground font-normal">(Opcional)</span></FormLabel>
              <FormControl>
                <Input placeholder="Nombre de la entidad" className="font-default rounded-md border-surface bg-background focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs text-muted-foreground">Sujeto obligado que realiza la actividad.</FormDescription>
              <FormMessage className="font-default text-destructive" />
            </FormItem>} />

        <FormField control={control} name="data_category" rules={{
        required: "Selecciona un giro comercial"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Giro (Actividad Vulnerable)</FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className="flex h-9 w-full rounded-md border border-surface bg-background px-3 py-1 text-base shadow-sm transition-colors outline-none focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-default appearance-none">
                    <option value="" disabled className="text-muted-foreground">Selecciona tu sector</option>
                    {DATA_CATEGORIES.map((category, i) => <option key={i} value={category} data-index={i}>
                        {category}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormDescription className="font-default text-xs text-muted-foreground">Sector correspondiente al Art. 17 LFPIORPI.</FormDescription>
              <FormMessage className="font-default text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="purpose" rules={{
      required: "La duda o comentario es obligatorio",
      minLength: {
        value: 10,
        message: "Por favor detalla un poco más tu consulta"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Duda o Requerimiento</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe tus necesidades de cumplimiento o dudas sobre la plataforma..." className="font-default rounded-md border-surface bg-background focus-visible:ring-primary min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs text-muted-foreground">Cita artículos de ley si es necesario para mayor precisión.</FormDescription>
            <FormMessage className="font-default text-destructive" />
          </FormItem>} />

      <FormField control={control} name="agree_terms" rules={{
      required: "Debes aceptar el aviso de privacidad"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-surface p-4 bg-card/50">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 shrink-0 rounded-sm border-primary text-primary focus:ring-primary focus:ring-offset-0 bg-background" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="font-default text-sm font-medium text-foreground cursor-pointer">
                Acepto el tratamiento de mis datos según el Aviso de Privacidad
              </FormLabel>
              <FormDescription className="font-default text-xs text-muted-foreground">
                Tus datos están protegidos bajo estándares de seguridad fintech.
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 flex items-start gap-3 rounded-md">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto rounded-md font-default font-semibold px-8 py-6 text-base bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Procesando...
          </> : <>
            <Send className="mr-2 size-5" />
            Solicitar asesoría técnica
          </>}
      </Button>
    </Form>;
}
export default function ConsultaAUnExperto() {
  return <section data-section-id="240633" id="consulta-a-un-experto" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card className="rounded-2xl shadow-md border-surface bg-card overflow-hidden">
          <CardHeader className="text-center pb-8 border-b border-surface/50 mb-6">
            <CardTitle className="text-3xl md:text-4xl font-bold font-default text-foreground tracking-tight">
              Consulta a un Experto
            </CardTitle>
            <CardDescription className="text-base md:text-lg mt-3 font-default text-muted-foreground max-w-2xl mx-auto">
              Resuelve tus dudas sobre el cumplimiento del Art. 17 LFPIORPI y descubre cómo Notalia automatiza tus expedientes y avisos ante la UIF.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <FormProvider formKey="consulta_experto_form_guide_lfpiorpi" formId="consulta_experto_form_guide_lfpiorpi" sectionName="consulta-a-un-experto" defaultValues={{
            full_name: "",
            email: "",
            organization: "",
            data_category: "",
            purpose: "",
            agree_terms: false
          }} submitText="Solicitar asesoría técnica" successMessage="Tu consulta ha sido recibida. Un especialista en PLD/AML se pondrá en contacto contigo a través del correo proporcionado en un plazo no mayor a 24 horas hábiles.">
              <DataRequestFormContent />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}